import { useEffect, useState } from 'react';

import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  FlagOutlined,
  MoreOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  DashboardVideo,
  DashboardVideoDetail,
  DashboardVideosListParams,
  DashboardVideosListResponse,
} from 'models/DashboardVideo';

import { DashboardVideoService } from 'services/DashboardVideoService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../components/Layout/Layout';
import appI18n from '../../config/i18n';
import CreateVideoModal from './Components/CreateVideoModal';

import './Videos.scss';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'videos.pageSize';
const DEFAULT_COUNTRY = 'CL';
const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

// Formatea segundos a mm:ss (o hh:mm:ss si supera la hora).
const formatDuration = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.round(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

function Videos() {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message, modal } = App.useApp();

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<DashboardVideo[]>([]);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [total, setTotal] = useState<number>(0);

  // Búsqueda por título (se envía como `q` al backend).
  const [term, setTerm] = useState<string>('');

  // Filtros aplicados (los que se mandan al backend). El estado arranca en
  // "Activo" por defecto.
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [active, setActive] = useState<boolean | undefined>(true);

  // Filtros en borrador (los que se editan dentro del popover).
  const [draftCountry, setDraftCountry] = useState<string>(DEFAULT_COUNTRY);
  const [draftActive, setDraftActive] = useState<boolean | undefined>(true);

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // Modal crear/editar.
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingVideo, setEditingVideo] = useState<DashboardVideoDetail | null>(
    null
  );
  const [editLoading, setEditLoading] = useState<boolean>(false);

  useEffect(() => {
    setPage(1);
  }, [country, active, term]);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, country, active, term]);

  const fetchData = async (silent = false) => {
    if (!silent) {
      setTableLoading(true);
    }

    const params: DashboardVideosListParams = {
      country,
      page,
      pageSize,
    };
    if (active !== undefined) params.active = active;
    if (term) params.q = term;

    const response = await DashboardVideoService.getDashboardVideos(params);

    if (response.success) {
      const data = response.data as DashboardVideosListResponse;
      setDataSource(data.data);
      setTotal(data.totalRows);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t('scenes.videos.scene.errors.fetch_list'),
        3.5
      );
    }

    if (!silent) {
      setTableLoading(false);
    }
  };

  // Aplica el término de búsqueda (Enter / clic en buscar / limpiar con la X).
  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleTableChange: TableProps<DashboardVideo>['onChange'] = (
    pagination
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }
  };

  const openEditModal = async (row: DashboardVideo) => {
    // Abrimos el modal de inmediato y mostramos un skeleton mientras llega
    // el detalle desde el backend.
    setEditingVideo(null);
    setModalMode('edit');
    setEditLoading(true);
    setIsModalOpen(true);

    const response = await DashboardVideoService.getDashboardVideoById(row.id);
    if (response.success) {
      setEditingVideo(response.data as DashboardVideoDetail);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.errors.fetch_detail'),
        3.5
      );
      setIsModalOpen(false);
    }
    setEditLoading(false);
  };

  const openCreateModal = () => {
    setEditingVideo(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleDelete = (row: DashboardVideo) => {
    modal.confirm({
      title: t('scenes.videos.scene.delete_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.videos.scene.delete_confirm.content', {
        title: row.title,
      }),
      okText: t('scenes.videos.scene.delete_confirm.ok'),
      cancelText: t('scenes.videos.scene.delete_confirm.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await DashboardVideoService.deleteDashboardVideo(
          row.id
        );
        if (response.success) {
          message.success(t('scenes.videos.scene.delete_confirm.success'), 2.5);
          // Si era el único de la página y no es la primera, retrocedemos una
          // página; si no, recargamos la página actual.
          if (dataSource.length === 1 && page > 1) {
            setPage((p) => p - 1);
          } else {
            fetchData(true);
          }
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t('scenes.videos.scene.delete_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const handleToggleStatus = (row: DashboardVideo) => {
    const newActive = !row.active;
    const key = newActive ? 'activate' : 'deactivate';

    modal.confirm({
      title: t(`scenes.videos.scene.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.videos.scene.status_confirm.content_${key}`, {
        title: row.title,
      }),
      okText: t(`scenes.videos.scene.status_confirm.ok_${key}`),
      cancelText: t('scenes.videos.scene.status_confirm.cancel'),
      okButtonProps: { danger: !newActive },
      onOk: async () => {
        const response = await DashboardVideoService.updateDashboardVideoStatus(
          row.id,
          {
            active: newActive,
          }
        );
        if (response.success) {
          message.success(
            t(`scenes.videos.scene.status_confirm.success_${key}`),
            2.5
          );
          // Refetch siempre: si el filtro de estado está activo, el video que
          // cambia de estado debe salir/entrar del listado; además el backend
          // puede limpiar el flag de video del mes al desactivar.
          fetchData(true);
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t('scenes.videos.scene.status_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const applyVideoOfMonth = async (
    row: DashboardVideo,
    isVideoOfMonth: boolean
  ) => {
    const response = await DashboardVideoService.updateVideoOfMonth(row.id, {
      isVideoOfMonth,
    });
    if (response.success) {
      message.success(
        t(
          isVideoOfMonth
            ? 'scenes.videos.scene.video_of_month_confirm.success_mark'
            : 'scenes.videos.scene.video_of_month_confirm.success_unmark'
        ),
        2.5
      );
      // Marcar uno desmarca al anterior del país: recargamos la lista.
      fetchData(true);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.video_of_month_confirm.error'),
        3.5
      );
    }
  };

  const handleToggleVideoOfMonth = (row: DashboardVideo) => {
    if (row.isVideoOfMonth) {
      // Desmarcar: confirmación simple.
      modal.confirm({
        title: t('scenes.videos.scene.video_of_month_confirm.title_unmark'),
        icon: <ExclamationCircleOutlined />,
        content: t(
          'scenes.videos.scene.video_of_month_confirm.content_unmark',
          {
            title: row.title,
          }
        ),
        okText: t('scenes.videos.scene.video_of_month_confirm.ok_unmark'),
        cancelText: t('scenes.videos.scene.video_of_month_confirm.cancel'),
        onOk: () => applyVideoOfMonth(row, false),
      });
      return;
    }

    // Marcar: avisamos que se desmarcará el video del mes actual del país.
    modal.confirm({
      title: t('scenes.videos.scene.video_of_month_confirm.title_mark'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.videos.scene.video_of_month_confirm.content_mark', {
        title: row.title,
      }),
      okText: t('scenes.videos.scene.video_of_month_confirm.ok_mark'),
      cancelText: t('scenes.videos.scene.video_of_month_confirm.cancel'),
      onOk: () => applyVideoOfMonth(row, true),
    });
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftCountry(country);
      setDraftActive(active);
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    setCountry(draftCountry);
    setActive(draftActive);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCountry(DEFAULT_COUNTRY);
    setDraftActive(undefined);
  };

  const activeFiltersCount =
    1 + // país siempre cuenta
    (active !== undefined ? 1 : 0);

  const getDropdownMenu = (row: DashboardVideo): MenuProps['items'] => {
    // La API responde 422 (cannot_mark_inactive) al marcar como video del mes
    // un video inactivo: deshabilitamos el item en ese caso.
    const markDisabled = !row.active && !row.isVideoOfMonth;

    return [
      {
        key: 'edit',
        label: (
          <span>
            <EditOutlined style={{ marginRight: '8px' }} />
            {t('scenes.videos.scene.dropdown.edit')}
          </span>
        ),
        onClick: () => openEditModal(row),
      },
      {
        key: 'video-of-month',
        disabled: markDisabled,
        label: (
          <span>
            {row.isVideoOfMonth ? (
              <StarOutlined style={{ marginRight: '8px' }} />
            ) : (
              <StarFilled style={{ marginRight: '8px' }} />
            )}
            {t(
              row.isVideoOfMonth
                ? 'scenes.videos.scene.dropdown.unmark_video_of_month'
                : 'scenes.videos.scene.dropdown.mark_video_of_month'
            )}
          </span>
        ),
        onClick: () => handleToggleVideoOfMonth(row),
      },
      {
        key: 'toggle-status',
        label: (
          <span>
            {row.active ? (
              <StopOutlined style={{ marginRight: '8px' }} />
            ) : (
              <CheckCircleOutlined style={{ marginRight: '8px' }} />
            )}
            {t(
              row.active
                ? 'scenes.videos.scene.dropdown.deactivate'
                : 'scenes.videos.scene.dropdown.activate'
            )}
          </span>
        ),
        onClick: () => handleToggleStatus(row),
      },
      { type: 'divider' },
      {
        key: 'delete',
        danger: true,
        label: (
          <span>
            <DeleteOutlined style={{ marginRight: '8px' }} />
            {t('scenes.videos.scene.dropdown.delete')}
          </span>
        ),
        onClick: () => handleDelete(row),
      },
    ];
  };

  const getCountryOptions = () =>
    COUNTRY_OPTIONS.map((opt) => {
      const code = opt.value.toLowerCase();
      return {
        value: opt.value,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <img
              src={`https://flagcdn.com/16x12/${code}.png`}
              srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
              width={16}
              height={12}
              alt=""
              style={{ display: 'block' }}
            />
            <span>
              {t(`scenes.videos.scene.countries.${opt.value}`, opt.value)}
            </span>
          </span>
        ),
      };
    });

  const statusOptions = [
    { value: true, label: t('scenes.videos.scene.statuses.active') },
    { value: false, label: t('scenes.videos.scene.statuses.inactive') },
  ];

  const formatDateCell = (isoDate: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(isoDate).format(`${datePart} h:mm A`);
  };

  const filtersContent = (
    <div style={{ width: 320 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('scenes.videos.scene.filters.country_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftCountry}
            onChange={(value) => setDraftCountry(value)}
            options={getCountryOptions()}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.videos.scene.filters.status_label')}
          style={{ marginBottom: 0 }}
        >
          <Select
            value={draftActive}
            onChange={(value) => setDraftActive(value as boolean | undefined)}
            options={statusOptions}
            style={{ width: '100%' }}
            placeholder={t('scenes.videos.scene.filters.status_placeholder')}
            allowClear
          />
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.videos.scene.filters.clear_filters')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.videos.scene.filters.apply_filters')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout title={''}>
      <div className="videos-scene">
        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.videos.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.videos.scene.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      <Space size={6}>
                        {t('scenes.videos.scene.filters.advanced_filters')}
                      </Space>
                    </Button>
                  </Badge>
                </Popover>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreateModal}
                >
                  {t('scenes.videos.scene.create_button')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<DashboardVideo>
            dataSource={dataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 900 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.videos.scene.table.columns.title')}
              key="title"
              render={(row: DashboardVideo) => (
                <div className="video-name-cell">
                  <button
                    type="button"
                    className="video-id-link"
                    onClick={() => openEditModal(row)}
                  >
                    {row.title}
                  </button>
                  <div
                    className="video-name-sub"
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span>
                      <FlagOutlined style={{ marginRight: 6 }} />
                      {t(
                        `scenes.videos.scene.countries.${row.countryCode}`,
                        row.countryCode
                      )}
                    </span>
                    {row.isVideoOfMonth && (
                      <Tag
                        color="gold"
                        icon={<StarFilled />}
                        bordered={false}
                        style={{
                          marginRight: 0,
                          fontSize: 11,
                          lineHeight: '16px',
                          padding: '0 6px',
                        }}
                      >
                        {t('scenes.videos.scene.table.video_of_month_tag')}
                      </Tag>
                    )}
                  </div>
                </div>
              )}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.videos.scene.table.columns.duration')}
              key="duration"
              render={(row: DashboardVideo) => (
                <span>{formatDuration(row.durationSeconds)}</span>
              )}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.videos.scene.table.columns.mandatory')}
              key="mandatory"
              render={(row: DashboardVideo) =>
                row.isMandatory ? (
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    {t('scenes.videos.scene.table.mandatory_tag')}
                  </Tag>
                ) : (
                  <Tag style={{ marginRight: 0 }}>
                    {t('scenes.videos.scene.table.recommended_tag')}
                  </Tag>
                )
              }
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.videos.scene.table.columns.status')}
              key="status"
              render={(row: DashboardVideo) => (
                <Tag
                  color={row.active ? 'green' : 'default'}
                  style={{ marginRight: 0 }}
                >
                  {row.active
                    ? t('scenes.videos.scene.statuses.active')
                    : t('scenes.videos.scene.statuses.inactive')}
                </Tag>
              )}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.videos.scene.table.columns.created')}
              key="createdAt"
              render={(row: DashboardVideo) => (
                <span>
                  {row.createdAt ? formatDateCell(row.createdAt) : '—'}
                </span>
              )}
            />

            <Column
              width={60}
              align="center"
              title=""
              key="actions"
              render={(row: DashboardVideo) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      <CreateVideoModal
        isOpen={isModalOpen}
        mode={modalMode}
        video={editingVideo}
        loading={editLoading}
        defaultCountry={country}
        handleModalCancel={() => setIsModalOpen(false)}
        onSaved={() => {
          message.success(
            modalMode === 'edit'
              ? t('scenes.videos.scene.create.edit_success')
              : t('scenes.videos.scene.create.success'),
            3
          );
          fetchData(true);
        }}
      />
    </Layout>
  );
}

export default Videos;
